import React, { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  brandAssetsMeta, listBrandAssets, uploadBrandAsset, createTextAsset, patchBrandAsset,
  deleteBrandAsset, brandAssetVersions, brandAssetRecs,
} from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Star, Trash, UploadSimple, MagnifyingGlass, ClockCounterClockwise, Warning, CheckCircle,
  Sparkle, PencilSimple, FolderSimple, Tag, Palette, TextT, Image as ImageIcon, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function TextAssetDialog({ open, onClose, category, meta, existing, clientId, onSaved }) {
  const [value, setValue] = useState("");
  const [name, setName] = useState("");
  React.useEffect(() => { setValue(existing?.value || ""); setName(existing?.name || ""); }, [existing, open]);
  const label = meta?.labels?.[category] || category || "";
  const save = async () => {
    if (!value.trim()) return toast.error("Enter a value");
    try {
      if (existing && !meta?.singleton_text?.includes(category)) {
        await patchBrandAsset(existing.id, { value, name: name || label });
      } else {
        await createTextAsset({ client_id: clientId, category, value, name: name || label });
      }
      toast.success(`${label} saved — every AI employee now uses it`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#14141F] border-white/10 text-white" data-testid="text-asset-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><TextT size={17} className="brand-text" /> {existing ? "Edit" : "Add"} {label}</DialogTitle>
          <DialogDescription className="text-[#7a7a7a] text-xs">Saved here, used everywhere — every AI employee references this automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!meta?.singleton_text?.includes(category) && (
            <div><Label className="label-caps">Name</Label>
              <Input data-testid="text-asset-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={label} className="bg-[#10101A] border-white/10 mt-1" /></div>
          )}
          <div><Label className="label-caps">{label}</Label>
            <Textarea data-testid="text-asset-value" value={value} onChange={(e) => setValue(e.target.value)} rows={category === "brand_colors" ? 3 : 5}
              placeholder={category === "brand_colors" ? "e.g. Primary #6B3DFF, Black #0a0a0a, Cream #F5EFE6" : `Enter your ${label.toLowerCase()}…`}
              className="bg-[#10101A] border-white/10 mt-1" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="text-asset-save" onClick={save} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetCard({ a, meta, onChanged, onHistory }) {
  const isImage = a.asset_type === "image";
  const warn = (a.validation?.warnings || []).length > 0;
  const fav = useMutation({
    mutationFn: () => patchBrandAsset(a.id, { favorite: !a.favorite }),
    onSuccess: onChanged,
  });
  const del = useMutation({
    mutationFn: () => deleteBrandAsset(a.id),
    onSuccess: () => { toast.success("Asset removed"); onChanged(); },
  });
  const [tagEdit, setTagEdit] = useState(false);
  const [tagVal, setTagVal] = useState((a.tags || []).join(", "));
  const saveTags = async () => {
    await patchBrandAsset(a.id, { tags: tagVal.split(",").map((t) => t.trim()).filter(Boolean) });
    setTagEdit(false); onChanged();
  };
  return (
    <div className="card-matte p-3" data-testid={`asset-card-${a.id}`}>
      {isImage ? (
        <img src={`${BACKEND}${a.url}`} alt={a.name} loading="lazy"
          className="w-full h-28 object-contain rounded-md mb-2 bg-[#10101A] border border-white/6" data-testid={`asset-preview-${a.id}`} />
      ) : (
        <div className="w-full h-28 rounded-md mb-2 bg-[#10101A] border border-white/6 p-2.5 overflow-hidden">
          <div className="text-[11px] text-[#9a9a9a] leading-snug line-clamp-5 whitespace-pre-wrap">{a.value}</div>
        </div>
      )}
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{a.name}</div>
          <div className="text-[10px] text-[#7a7a7a]">{meta?.labels?.[a.category] || a.category}</div>
        </div>
        <button data-testid={`asset-fav-${a.id}`} onClick={() => fav.mutate()} className="shrink-0">
          <Star size={15} weight={a.favorite ? "fill" : "regular"} className={a.favorite ? "text-amber-400" : "text-[#5a5a5a] hover:text-amber-400"} />
        </button>
      </div>
      {(a.tags || []).length > 0 && !tagEdit && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {a.tags.map((t) => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-white/6 text-[#9a9a9a]">{t}</span>)}
        </div>
      )}
      {tagEdit && (
        <div className="flex gap-1 mt-1.5">
          <Input value={tagVal} onChange={(e) => setTagVal(e.target.value)} placeholder="tag1, tag2" className="bg-[#10101A] border-white/10 h-6 text-[10px]" data-testid={`asset-tag-input-${a.id}`} />
          <button onClick={saveTags} className="text-[10px] text-emerald-400" data-testid={`asset-tag-save-${a.id}`}>Save</button>
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 text-[10px]">
        {warn ? (
          <span className="flex items-center gap-1 text-amber-300" title={(a.validation?.warnings || []).join(" ")}><Warning size={11} weight="fill" /> Check</span>
        ) : isImage && a.validation ? (
          <span className="flex items-center gap-1 text-emerald-400" title={`${a.validation.width}×${a.validation.height}`}><CheckCircle size={11} weight="fill" /> Valid</span>
        ) : null}
        {a.version_count > 0 && (
          <button data-testid={`asset-history-${a.id}`} onClick={() => onHistory(a)} className="flex items-center gap-0.5 text-[#9a9a9a] hover:text-white">
            <ClockCounterClockwise size={11} /> {a.version_count}
          </button>
        )}
        <button data-testid={`asset-tags-${a.id}`} onClick={() => setTagEdit((v) => !v)} className="flex items-center gap-0.5 text-[#7a7a7a] hover:text-white"><Tag size={11} /></button>
        <button data-testid={`asset-delete-${a.id}`} onClick={() => del.mutate()} className="ml-auto text-[#5a5a5a] hover:text-red-400"><Trash size={12} /></button>
      </div>
    </div>
  );
}

export default function BrandAssetsPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [uploadCat, setUploadCat] = useState("primary_logo");
  const [textDialog, setTextDialog] = useState(null); // {category, existing}
  const [historyFor, setHistoryFor] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const metaQ = useQuery({ queryKey: ["ba-meta"], queryFn: brandAssetsMeta, staleTime: 600000 });
  const meta = metaQ.data;
  const assetsQ = useQuery({
    queryKey: ["brand-assets", activeId, folder, q, favOnly],
    queryFn: () => listBrandAssets(activeId, { ...(folder !== "all" ? { folder } : {}), ...(q ? { q } : {}), ...(favOnly ? { favorites: true } : {}) }),
    enabled: !!activeId,
  });
  const recsQ = useQuery({ queryKey: ["ba-recs", activeId], queryFn: () => brandAssetRecs(activeId), enabled: !!activeId });
  const versionsQ = useQuery({
    queryKey: ["ba-versions", historyFor?.id],
    queryFn: () => brandAssetVersions(historyFor.id), enabled: !!historyFor,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["brand-assets", activeId] }); qc.invalidateQueries({ queryKey: ["ba-recs", activeId] }); };

  const doUpload = async (files, category) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("client_id", activeId);
      fd.append("category", category || uploadCat);
      try {
        const r = await uploadBrandAsset(fd);
        const warns = r.validation?.warnings || [];
        if (warns.length) toast.warning(`${file.name}: ${warns[0]}`);
        else toast.success(`${file.name} uploaded`);
      } catch (e) { toast.error(e?.response?.data?.detail || `${file.name} upload failed`); }
    }
    refresh();
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const files = [...(e.dataTransfer?.files || [])];
    if (files.length) doUpload(files);
  };

  const assets = assetsQ.data || [];
  const textCatsInFolder = useMemo(() => {
    if (!meta) return [];
    const cats = folder === "all" ? Object.keys(meta.text_categories)
      : (meta.folders.find((f) => f.key === folder)?.categories || []).filter((c) => meta.text_categories[c]);
    return cats;
  }, [meta, folder]);
  const existingByCat = useMemo(() => {
    const m = {};
    assets.forEach((a) => { if (!m[a.category]) m[a.category] = a; });
    return m;
  }, [assets]);
  const recs = recsQ.data;

  return (
    <PageContainer>
      <PageHeader eyebrow="Agency" title="Brand Assets"
        subtitle={`The single source of truth for ${activeClient?.name || "this brand"} — every AI employee automatically uses these assets instead of inventing branding.`} />

      {recs && (
        <div className="card-matte p-4 mb-5" data-testid="ba-recommendations">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Sparkle size={15} className="brand-text" weight="fill" /> AI Asset Recommendations</div>
            <div className="text-xs text-[#9a9a9a]">Brand completeness: <span className={`font-bold ${recs.completeness >= 60 ? "text-emerald-400" : "brand-text"}`} data-testid="ba-completeness">{recs.completeness}%</span> ({recs.filled}/{recs.total} categories)</div>
          </div>
          {(recs.recommendations || []).length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {recs.recommendations.map((r) => (
                <div key={r.category} className="rounded-lg border border-[#6B3DFF]/20 bg-[#6B3DFF]/[0.04] px-3 py-2" data-testid={`ba-rec-${r.category}`}>
                  <div className="text-xs font-semibold text-[#C39BFF]">Add: {r.label}</div>
                  <div className="text-[10px] text-[#9a9a9a] mt-0.5 leading-snug">{r.why}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> All priority brand assets are on file — your AI team is fully briefed.</div>}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a5a5a]" />
          <Input data-testid="ba-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets, tags…"
            className="bg-[#10101A] border-white/10 pl-8 h-8 w-52 text-xs" />
        </div>
        <button data-testid="ba-folder-all" onClick={() => setFolder("all")}
          className={`text-xs px-2.5 py-1.5 rounded-lg border flex items-center gap-1 ${folder === "all" ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
          <FolderSimple size={13} /> All
        </button>
        {(meta?.folders || []).map((f) => (
          <button key={f.key} data-testid={`ba-folder-${f.key}`} onClick={() => setFolder(f.key)}
            className={`text-xs px-2.5 py-1.5 rounded-lg border ${folder === f.key ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
            {f.label}
          </button>
        ))}
        <button data-testid="ba-fav-filter" onClick={() => setFavOnly((v) => !v)}
          className={`text-xs px-2.5 py-1.5 rounded-lg border flex items-center gap-1 ${favOnly ? "border-amber-400/40 bg-amber-500/10 text-amber-300" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
          <Star size={13} weight={favOnly ? "fill" : "regular"} /> Favorites
        </button>
      </div>

      {/* Upload zone */}
      <div data-testid="ba-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)} onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed px-4 py-5 mb-5 text-center transition-colors ${dragging ? "border-[#6B3DFF] bg-[#6B3DFF]/5" : "border-white/12"}`}>
        <UploadSimple size={22} className="mx-auto mb-1.5 text-[#7a7a7a]" />
        <div className="text-sm text-[#cfcfcf]">Drag & drop images here, or</div>
        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
          <select data-testid="ba-upload-category" value={uploadCat} onChange={(e) => setUploadCat(e.target.value)}
            className="bg-[#10101A] border border-white/12 rounded-lg px-2 py-1.5 text-xs text-[#cfcfcf]">
            {Object.entries(meta?.image_categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button data-testid="ba-browse-btn" size="sm" onClick={() => fileRef.current?.click()}
            className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-8 text-xs"><ImageIcon size={13} className="mr-1" /> Browse Files</Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf" multiple hidden
            onChange={(e) => { doUpload([...(e.target.files || [])]); e.target.value = ""; }} data-testid="ba-file-input" />
        </div>
        <div className="text-[10px] text-[#5a5a5a] mt-1.5">PNG, JPG, WEBP, SVG or PDF · validated automatically</div>
      </div>

      {/* Text assets quick-add */}
      {textCatsInFolder.length > 0 && (
        <div className="mb-5" data-testid="ba-text-quickadd">
          <div className="label-caps mb-2 flex items-center gap-1"><Palette size={13} className="brand-text" /> Brand Info Assets</div>
          <div className="flex flex-wrap gap-2">
            {textCatsInFolder.map((c) => {
              const has = !!existingByCat[c];
              return (
                <button key={c} data-testid={`ba-text-add-${c}`} onClick={() => setTextDialog({ category: c, existing: meta.singleton_text.includes(c) ? existingByCat[c] : null })}
                  className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${has ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.05]" : "border-white/12 text-[#9a9a9a] hover:text-white hover:border-[#6B3DFF]/40"}`}>
                  {has ? <PencilSimple size={12} /> : <Plus size={12} />} {meta.text_categories[c]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Asset grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="ba-grid">
        {assets.map((a) => <AssetCard key={a.id} a={a} meta={meta} onChanged={refresh} onHistory={setHistoryFor} />)}
      </div>
      {!assets.length && !assetsQ.isLoading && (
        <div className="text-center text-sm text-[#7a7a7a] py-10" data-testid="ba-empty">No assets here yet — upload images above or add brand info assets.</div>
      )}

      <TextAssetDialog open={!!textDialog} onClose={() => setTextDialog(null)} category={textDialog?.category}
        meta={meta} existing={textDialog?.existing} clientId={activeId} onSaved={refresh} />

      {/* Version history */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg" data-testid="ba-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><ClockCounterClockwise size={16} className="brand-text" /> Version History — {historyFor?.name}</DialogTitle>
            <DialogDescription className="text-[#7a7a7a] text-xs">Previous versions of this asset, newest first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(versionsQ.data || []).slice().reverse().map((v) => (
              <div key={v.index} className="flex items-center gap-3 rounded-lg border border-white/8 p-2.5" data-testid={`ba-version-${v.index}`}>
                {v.is_image ? <img src={`${BACKEND}${v.url}`} alt="" className="h-12 w-12 object-contain rounded bg-[#10101A]" />
                  : <div className="text-[10px] text-[#9a9a9a] flex-1 line-clamp-2">{v.value}</div>}
                <div className="text-[10px] text-[#7a7a7a] ml-auto shrink-0">{(v.at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
            {!(versionsQ.data || []).length && <div className="text-xs text-[#5a5a5a]">No previous versions.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
